import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilCallback, useRecoilValue, useSetRecoilState } from "recoil";

import {
  useModalConfirm,
  useNewStationRouteTemplate,
  useRebuildRoute,
  useRideDtoActions,
} from "@ryde/redux";
import {
  applySavedMlPathStationsToRideStations,
  castErrorResult,
  clearRideMlPathIgnoreReason,
  ConfirmResult,
  createErrorResult,
  editFormDataAF,
  EditFormMode,
  errors,
  getRealRouteStationIds,
  getRideDismissedMlPathStationRouteId,
  IRideDto,
  IRideMetadata,
  IStationRouteDto,
  isValidPositiveNumber,
  Log,
  RebuildRouteTypeEnum,
  RideCrudModeKeyTuple,
  rideHasIgnoredMlPath,
  routeHasRoutingWaypoints,
  StationRouteApi,
  stationRouteHasUnsavedStationsChangeAtom,
  stationRouteMatchingMlPathIdsAtom,
  stationRouteMatchingMlPathUpdateIdAtom,
  systemSettingsDtoAtom,
  useDrawers,
  warnAndToastSimpleResult,
} from "@ryde/shared";

import { useRideSaveRouteActions } from "./use-ride-save-route-actions";

export const useRideSaveRoutePath = () => {
  const { t } = useTranslation();
  const { confirmDialogResult } = useModalConfirm();
  const systemParams = useRecoilValue(systemSettingsDtoAtom);
  const { openStationRouteEditFormDrawer } = useDrawers();
  const rideDto = useRecoilValue<Partial<IRideDto> | undefined>(
    editFormDataAF(RideCrudModeKeyTuple),
  );
  const { updateEditingRideMetadataRideDate } = useRideDtoActions();
  const { rebuildRideRoute } = useRebuildRoute();

  const { deleteRideRouteAction } = useRideSaveRouteActions();

  const { getNewStationRouteTemplate } = useNewStationRouteTemplate();
  const setStationRouteDto = useSetRecoilState<
    Partial<IStationRouteDto> | undefined
  >(editFormDataAF(["stationRoute", EditFormMode.UnboundDrawer]));
  const setStationRouteHasUnsavedChange = useSetRecoilState(
    stationRouteHasUnsavedStationsChangeAtom,
  );
  const setMatchingMlPathIds = useSetRecoilState(
    stationRouteMatchingMlPathIdsAtom,
  );
  const setMatchingMlPathUpdateId = useSetRecoilState(
    stationRouteMatchingMlPathUpdateIdAtom,
  );
  const getCurrentRideDto = useRecoilCallback(
    ({ snapshot }) =>
      async (): Promise<Partial<IRideDto> | undefined> =>
        snapshot.getPromise(editFormDataAF(RideCrudModeKeyTuple)),
    [],
  );

  const saveStationRouteClickHandler = useEvent(
    async (isRouteDeleted?: boolean) => {
      if (!rideDto) {
        Log.warnAndToast(t(errors.ride.missingRide));
        return;
      }

      if (!isValidPositiveNumber(rideDto.id)) {
        Log.infoAndToast(t("ride.SAVE_ROUTE.SAVE_RIDE_FIRST"));
        return;
      }

      const isMlPathIgnored = rideHasIgnoredMlPath(rideDto.metadata);
      const dismissedMlPathRouteId = getRideDismissedMlPathStationRouteId(
        rideDto.metadata,
      );
      const activeCustomStationRouteId =
        rideDto.metadata?.customStationRouteId ?? undefined;
      const preferredMatchingId =
        activeCustomStationRouteId ?? dismissedMlPathRouteId ?? undefined;
      const matchingResult = await StationRouteApi.findMatchingMlPaths({
        stationIds: getRealRouteStationIds(rideDto.metadata?.stationsInfo),
        avoidTolls: systemParams?.metadata?.avoidTolls,
        preferredId: preferredMatchingId,
      });
      const matchingIds = matchingResult?.success
        ? (matchingResult.data?.ids ?? [])
        : [];
      const matchingUpdateId = matchingResult?.success
        ? (matchingResult.data?.updateId ?? null)
        : null;
      setMatchingMlPathIds(matchingIds);
      setMatchingMlPathUpdateId(matchingUpdateId);

      const shouldPrefillFromRide =
        !activeCustomStationRouteId ||
        Boolean(isRouteDeleted) ||
        isMlPathIgnored ||
        Boolean(matchingUpdateId);

      // Canonical customized path for this station set, else new / active id.
      let stationRouteDrawerId: number | undefined =
        matchingUpdateId ?? activeCustomStationRouteId;
      if (isRouteDeleted && !matchingUpdateId) {
        stationRouteDrawerId = undefined;
      }

      const rideStations = rideDto.metadata?.stationsInfo;
      const rideHasWaypoints = routeHasRoutingWaypoints(rideStations);

      const prefillTemplate = shouldPrefillFromRide
        ? getNewStationRouteTemplate({
            rideDto,
            systemParams,
          })
        : undefined;

      if (prefillTemplate && stationRouteDrawerId) {
        prefillTemplate.id = stationRouteDrawerId;
      }

      // openAsyncDrawer clears edit-form atoms before mount — set prefill AFTER open starts.
      const drawerResultPromise = openStationRouteEditFormDrawer({
        id: stationRouteDrawerId,
        rideOpenerId: rideDto.id,
        mode: EditFormMode.UnboundDrawer,
      });

      if (prefillTemplate) {
        setStationRouteDto(prefillTemplate);
        if (rideHasWaypoints) {
          setStationRouteHasUnsavedChange(true);
        }
        Log.debug(
          "Station route prefilled from ride (waypoints transferred as MLPath)",
          {
            stationRouteDrawerId,
            isMlPathIgnored,
            stationsCount: prefillTemplate.computedData?.stationInfos?.length,
          },
        );
      }

      const result = await drawerResultPromise;

      if (result.success && result.data) {
        Log.debug("StationRoute drawer result data", result.data);
        const resultData = result.data as IStationRouteDto;
        const savedRouteId = resultData?.id;
        const currentRideDto = (await getCurrentRideDto()) ?? rideDto;
        // Apply saved ML path stations onto the current ride (do not replay a pre-drawer snapshot).
        const stationsFromSavedMlPath = applySavedMlPathStationsToRideStations({
          rideStations: currentRideDto.metadata?.stationsInfo,
          stationRoute: resultData,
        });
        const updatedMetadata: IRideMetadata = clearRideMlPathIgnoreReason({
          ...currentRideDto.metadata,
          stationsInfo: stationsFromSavedMlPath,
          customStationRouteId: savedRouteId,
          usedCachedRouteId: savedRouteId,
          excludedStationRouteIds:
            resultData?.virtualData?.excludedStationRouteIds ??
            currentRideDto.metadata?.excludedStationRouteIds,
        });
        updateEditingRideMetadataRideDate({
          rideId: currentRideDto.id,
          updatedMetadata,
        });
        await rebuildRideRoute(
          { ...currentRideDto, metadata: updatedMetadata },
          { rebuildType: RebuildRouteTypeEnum.Default, optimize: false },
        );
        Log.infoAndToast(t("ride.SAVE_ROUTE.SAVED_SUCCESSFULLY"));
      } else {
        Log.debug("Station route not saved with result", result);

        if (result.error) {
          warnAndToastSimpleResult(castErrorResult(result), t);
        }
      }
    },
  );

  const deleteStationRouteHandler = useEvent(
    async (formStationRouteId?: number) => {
      if (!rideDto) {
        Log.warnAndToast(t(errors.ride.missingRide));
        return;
      }

      const confirmationResult = await confirmDialogResult({
        title: t("ride.SAVE_ROUTE.SAVED_PATH_CONFIRM_TITLE"),
        message: t("ride.SAVE_ROUTE.SAVED_PATH_DELETE_CONFIRM_TEXT"),
      });

      if (confirmationResult !== ConfirmResult.OK) {
        Log.infoAndToast(
          t("stationRoute.DELETE_STATION_ROUTE_CANCELED_BY_USER"),
        );
        return createErrorResult(
          "stationRoute.DELETE_STATION_ROUTE_CANCELED_BY_USER",
          "Station route delete canceled by user",
        );
      }

      const result = await deleteRideRouteAction(rideDto, formStationRouteId);

      if (result.success) {
        Log.infoAndToast(t("ride.SAVE_ROUTE.DELETED_SUCCESSFULLY"));
      } else {
        warnAndToastSimpleResult(castErrorResult(result), t);
      }

      return result;
    },
  );

  return {
    saveStationRouteClickHandler,
    deleteStationRouteHandler,
    stationRouteId: rideDto?.metadata?.customStationRouteId,
    isMlPathIgnored: rideHasIgnoredMlPath(rideDto?.metadata),
    ignoreMLPathReason: rideDto?.metadata?.ignoreMLPathReason,
    rideId: rideDto?.id,
    rideDto,
  };
};
